import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Spin, Card, Row, Col, Statistic } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { 
    CalculatorOutlined,
    EditOutlined,
    BankOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/ui";

interface PeriodBalance {
    id: number;
    accountingPeriodId: number;
    accountCode: string;
    accountName?: string;
    openingBalance: number;
    debitTotal: number;
    creditTotal: number;
    closingBalance: number;
    lastUpdated: string;
    createdAt: string;
    accountingPeriod?: {
        periodId: string;
        year: number;
        month: number;
        startDate: string;
        endDate: string;
        status: number;
    };
    account?: {
        accountCode: string;
        accountName: string;
        accountType: number;
    };
}

const getStatusText = (status: number) => {
    switch (status) {
        case 0: return "Open";
        case 1: return "Closed";
        case 2: return "Locked";
        default: return "Unknown";
    }
};

const getStatusColor = (status: number) => {
    switch (status) {
        case 0: return "warning";
        case 1: return "success";
        case 2: return "error";
        default: return "default";
    }
};

export const PeriodBalancesShow = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow<PeriodBalance>({
        resource: "period-balances",
        id,
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const netMovement = (record?.debitTotal || 0) - (record?.creditTotal || 0);
    const accountName = record?.account?.accountName || record?.accountName || record?.accountCode || "Account";

    return (
        <div className="page-container">
            <PageHeader
                title={`${accountName} - Period Balance`}
                titleIcon={<CalculatorOutlined />}
                breadcrumbs={[
                    { title: "Accounting", href: "/accounting" },
                    { title: "Period Balances", href: "/period-balances" },
                    { title: record?.accountCode || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/period-balances/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Account</div>
                        <Tag color="blue">{record?.accountCode || "-"}</Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Opening Balance</div>
                        <div className="mini-stat-value">${(record?.openingBalance || 0).toFixed(2)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Closing Balance</div>
                        <div className="mini-stat-value text-primary">${(record?.closingBalance || 0).toFixed(2)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Net Movement</div>
                        <div className="mini-stat-value" style={{ color: netMovement >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                            ${netMovement.toFixed(2)}
                        </div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Account Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">
                            <BankOutlined style={{ marginRight: 8 }} />
                            Account Information
                        </h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Account Code:</span>
                                <Tag color="blue" style={{ fontFamily: 'monospace' }}>{record?.accountCode || "-"}</Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Account Name:</span>
                                <span className="info-value font-semibold">{accountName}</span>
                            </div>
                            {record?.account?.accountType !== undefined && (
                                <div className="info-item">
                                    <span className="info-label">Account Type:</span>
                                    <span className="info-value">
                                        {["Asset", "Liability", "Equity", "Revenue", "Expense"][record.account.accountType] || "-"}
                                    </span>
                                </div>
                            )}
                            <div className="info-item">
                                <span className="info-label">Last Updated:</span>
                                <span className="info-value">
                                    {record?.lastUpdated ? <DateField value={record.lastUpdated} format="YYYY-MM-DD HH:mm:ss" /> : "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Period Information Card */}
                {record?.accountingPeriod && (
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Period Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Period:</span>
                                    <span className="info-value font-semibold">{record.accountingPeriod.periodId}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Period Status:</span>
                                    <Tag color={getStatusColor(record.accountingPeriod.status)}>
                                        {getStatusText(record.accountingPeriod.status)}
                                    </Tag>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Start Date:</span>
                                    <span className="info-value">
                                        <DateField value={record.accountingPeriod.startDate} format="YYYY-MM-DD" />
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">End Date:</span>
                                    <span className="info-value">
                                        <DateField value={record.accountingPeriod.endDate} format="YYYY-MM-DD" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Balance Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Balance Information</h3>
                    </div>
                    <div className="info-card-body">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Opening Balance"
                                        value={record?.openingBalance || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '16px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Total Debits"
                                        value={record?.debitTotal || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '16px', color: 'var(--color-success)' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Total Credits"
                                        value={record?.creditTotal || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '16px', color: 'var(--color-danger)' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Closing Balance"
                                        value={record?.closingBalance || 0}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ fontSize: '16px', color: 'var(--color-primary)', fontWeight: 'bold' }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            <Col xs={24} sm={12}>
                                <Card size="small" className="text-center">
                                    <Statistic
                                        title="Net Movement"
                                        value={netMovement}
                                        precision={2}
                                        prefix="$"
                                        valueStyle={{ 
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: netMovement >= 0 ? 'var(--color-success)' : 'var(--color-danger)'
                                        }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Audit Information */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Audit Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Created Date:</span>
                                <span className="info-value">
                                    {record?.createdAt ? <DateField value={record.createdAt} format="YYYY-MM-DD HH:mm:ss" /> : "-"}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Last Updated:</span>
                                <span className="info-value">
                                    {record?.lastUpdated ? <DateField value={record.lastUpdated} format="YYYY-MM-DD HH:mm:ss" /> : "-"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
